import React from 'react';
import { Box, Grid, InputAdornment, Typography } from '@mui/material';
import SparkIcon from 'components/SVG/SparkIcon';
import { Calender } from 'components/SVG';
import { SparkButton, SparkDateRangePicker } from 'sharedComponents/UI';
import type { JoinedSparks } from 'types/Spark/JoinedSpark';
import SparkSearchAutocomplete from 'components/common/SparkSearchAutocomplete';
import { HistoryFilter } from '.';
import 'dayjs/locale/en'; // Import English locale or the desired locale
import CloseIcon from '@mui/icons-material/Close';
import { useResize } from 'hooks/common';
import moment, { Moment } from 'moment';
import { useSparksByID } from 'hooks/spark';

const SparkFilterButton = ({
  handleClick,
  sparkID,
  isDisabled = false,
}: {
  handleClick: () => void;
  sparkID: string;
  isDisabled?: boolean;
}) => {
  const { joinedSpark, isLoading } = useSparksByID(sparkID);

  if (isLoading) {
    return (
      <SparkButton
        variant='outlined'
        startIcon={<SparkIcon />}
        endIcon={<CloseIcon />}
        onClick={() => {}}
        disabled={true}
        sx={{
          height: '2.5rem',
          backgroundColor: '#ffffff',
          color: '#000000',
          textTransform: 'capitalize',
        }}
      >
        Loading...
      </SparkButton>
    );
  }
  return (
    <SparkButton
      variant='outlined'
      startIcon={<SparkIcon />}
      endIcon={<CloseIcon />}
      onClick={handleClick}
      disabled={isDisabled}
      sx={{
        height: '2.5rem',
        backgroundColor: '#ffffff',
        color: '#000000',
        textTransform: 'capitalize',
      }}
    >
      {joinedSpark?.attributes.nameforHuman}
    </SparkButton>
  );
};
function formatDateInCustomFormat(date: Moment): string {
  const formattedDate = moment(date).format("D MMM 'YY");
  return formattedDate;
}

type SparksHistorySearchProps = {
  resetFilters: () => void;
  historyFilter: HistoryFilter;
  setHistoryFilter: React.Dispatch<React.SetStateAction<HistoryFilter>>;
};

function SparksHistorySearch({
  historyFilter,
  resetFilters,
  setHistoryFilter,
}: SparksHistorySearchProps) {
  const { isMobile } = useResize();
  const handleSparkClick = (spark: JoinedSparks) => {
    setHistoryFilter((prev) => {
      return {
        ...prev,
        sparkIDs: [...(prev?.sparkIDs || []), spark.id],
      };
    });
  };

  const handleSparkChipClick = (sparkID: string) => {
    setHistoryFilter((prev) => {
      return {
        ...prev,
        sparkIDs:
          prev?.sparkIDs?.filter((prevSparkID) => prevSparkID !== sparkID) ||
          [],
      };
    });
  };

  const sparkIDs = historyFilter.sparkIDs;

  return (
    <>
      <Box display='flex' alignItems='center' justifyContent='space-between'>
        <Typography
          variant='h4'
          sx={{
            fontFamily: 'Mont-semibold',
          }}
        >
          History
        </Typography>
      </Box>

      <Box
        display='flex'
        gap={2}
        width='100%'
        flexDirection={isMobile ? 'column' : 'row'}
        marginTop={2}
      >
        <SparkDateRangePicker
          setHistoryFilter={setHistoryFilter}
          historyFilter={historyFilter}
        />
        <SparkSearchAutocomplete
          onSparkClick={handleSparkClick}
          placeholder='Sparks'
          shouldRenderOrSuggestion={false}
          endAdornment={
            <InputAdornment position='start'>
              <SparkIcon />
            </InputAdornment>
          }
        />
      </Box>
      <Grid container item mt={3} display='flex' alignItems='center' gap={2}>
        <Grid item>
          <SparkButton
            variant='outlined'
            startIcon={<Calender />}
            sx={{
              height: '2.5rem',
              backgroundColor: '#ffffff',
              color: '#000000',
              textTransform: 'capitalize',
            }}
          >
            {formatDateInCustomFormat(historyFilter.dateRange.startDate)}
            <p
              style={{
                margin: '0 0.5rem',
                textTransform: 'lowercase',
                fontFamily: 'Mont-Semibold',
              }}
            >
              to
            </p>
            {formatDateInCustomFormat(historyFilter.dateRange.endDate)}
          </SparkButton>
        </Grid>

        {sparkIDs?.map((sparkID) => (
          <Grid item>
            <SparkFilterButton
              sparkID={sparkID}
              handleClick={() => handleSparkChipClick(sparkID)}
            />
          </Grid>
        ))}

        {!!historyFilter && (
          <Grid item>
            <SparkButton
              variant='outlined'
              onClick={resetFilters}
              sx={{
                height: '2.5rem',
                backgroundColor: '#ECEFFE',
                color: '#6267AC',
                textTransform: 'capitalize',
                '&:hover': {
                  color: '#6267AC',
                  backgroundColor: '#ECEFFE',
                },
              }}
            >
              Clear All
            </SparkButton>
          </Grid>
        )}
      </Grid>
    </>
  );
}

export default SparksHistorySearch;
